import React, { FunctionComponent } from 'react'

import { ClinTheme } from '../../ClinTheme'
import { ClinSpinner } from '../../components/ClinSpinner'
import { StyledSpinnerContainer } from '../../components/ClinSpinner/ClinSpinner.styles'
import { ClinText } from '../../components/ClinText'
import { IDefaultStyleProps, IProgramSummary } from '../../types'
import { CountryDto } from '../../types/swaggerTypes'
import {
  StyledProgramBrandText,
  StyledProgramContainer,
  StyledProgramItemContentLeft,
  StyledProgramItemContentRight,
  StyledProgramPillButton,
  StyledProgramEnrolled,
  StyledProgramRowItem
} from './ProgramsTable.styles'
import { ClinIcon } from '../../components/ClinIcon'
import { ClinIconPathName } from '../../components/ClinIcon/ClinIcon.paths'
import { NotFound } from '../NotFound'
import { useTranslation } from 'react-i18next'

interface IProgramsTableProps extends IDefaultStyleProps {
  /** Whether table is loading or not */
  isLoading: boolean
  /** The array of orders coming through */
  programs?: IProgramSummary[]
  /** The initial query */
  initialQuery?: string
  /** If the user is MA only or not */
  isMaUser?: boolean
  /** Supplied contact data */
  supportContact?: CountryDto
  /** Handle the row clicked and return id */
  handleRowClicked?: (orderId: number) => void
  /** When a search query is entered and ENTER pressed */
  handleOnSearch?: (query: string) => void
}

export const ProgramsTable: FunctionComponent<IProgramsTableProps> = ({
  isLoading = true,
  programs,
  initialQuery,
  handleRowClicked
}) => {
  const { t } = useTranslation()

  const getStatus = (programStatus: string) => {
    const status = programStatus?.toLowerCase?.() || ''

    if (status === 'open for resupply') {
      return 'Resupply only'
    }

    // Handle "Closed" status - should display as "Closed", not "Register your interest"
    if (status === 'closed') {
      return 'Closed'
    }

    // Handle null/empty status and other cases that should show "Register your interest"
    if (
      !status ||
      status === 'register your interest' ||
      status === 'not available in your country'
    ) {
      return 'Register your interest'
    }

    return programStatus
  }

  return (
    <>
      {isLoading && (
        <StyledSpinnerContainer>
          <ClinSpinner size={ClinTheme.space[7]} />
        </StyledSpinnerContainer>
      )}

      {!isLoading && programs && (
        <>
          {programs?.length > 0 ? (
            <StyledProgramContainer>
              {programs?.map((program: IProgramSummary, index: number) => {
                const programId = program.programId

                return (
                  <StyledProgramRowItem
                    key={`program_${programId}_${index}`}
                    onClick={() => {
                      handleRowClicked && handleRowClicked(programId)
                    }}
                  >
                    <StyledProgramItemContentLeft>
                      <ClinText
                        style={{
                          display: 'flex'
                        }}
                        as="h3"
                        fontWeight={ClinTheme.fontWeights.bold}
                        fontSize={ClinTheme.fontSizes[1]}
                        lineHeight={'24px'}
                        width="fit-content"
                      >
                        {program.programName}
                        {program.userEnrolStatus === 'Y' && (
                          <StyledProgramEnrolled>
                            {t('access_programs:enrolled_title')}
                          </StyledProgramEnrolled>
                        )}
                      </ClinText>
                      <StyledProgramBrandText as="p">
                        {program.sponsor}
                      </StyledProgramBrandText>
                    </StyledProgramItemContentLeft>
                    <StyledProgramItemContentRight>
                      <StyledProgramPillButton
                        status={getStatus(program.status || '')}
                      >
                        {getStatus(program.status || '')}
                      </StyledProgramPillButton>
                      <ClinIcon
                        iconSize={16}
                        iconFill={ClinTheme.colors.primaryMid}
                        iconName={ClinIconPathName.ChevronRight}
                      />
                    </StyledProgramItemContentRight>
                  </StyledProgramRowItem>
                )
              })}
            </StyledProgramContainer>
          ) : (
            <NotFound searchInput={initialQuery} />
          )}
        </>
      )}
    </>
  )
}
